import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

const IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/2f8qkimi_cheere%20photo.png";

const divisions = [
  { name: "6U Mini", detail: "Ages 5-6 · Intro" },
  { name: "8U Peewee", detail: "Ages 7-8" },
  { name: "10U Junior", detail: "Ages 9-10" },
  { name: "12U Youth", detail: "Ages 11-12" },
];

export default function Cheer() {
  return (
    <div data-testid="cheer-page">
      <section className="relative bg-scu-obsidian text-white overflow-hidden">
        <img src={IMG} alt="Utes Cheer" className="absolute inset-0 h-full w-full object-cover opacity-55" style={{ objectPosition: "center 30%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-scu-obsidian/60 to-scu-obsidian" />
        <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-24 md:py-32">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Program</div>
          <h1 className="mt-4 font-bebas text-6xl md:text-8xl tracking-wider">Utes Cheer</h1>
          <p className="mt-4 max-w-2xl text-white/80 text-lg">
            Skill, spirit, and community. SCU Cheer matches the intensity of our football program with elite
            coaching and performance at every division.
          </p>
          <div className="mt-8">
            <Button asChild className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-12 px-6">
              <Link to="/registration">Join Cheer <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
        <div className="stripe-divider" />
      </section>

      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Divisions</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Cheer Divisions</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {divisions.map((d) => (
            <div key={d.name} className="border border-scu-smoke/20 bg-white p-6 rounded-md hover:border-scu-purple hover:-translate-y-1 transition-transform duration-200">
              <div className="font-bebas text-3xl text-scu-purple tracking-wide">{d.name}</div>
              <div className="text-sm text-scu-smoke mt-2">{d.detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Coaching Staff</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Utes Cheer Coaches</h2>
        <p className="mt-2 text-scu-smoke max-w-2xl">Head coach and instructors will be introduced here as they are announced.</p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cheer-coaches-grid">
          {[
            { role: "Cheer Coordinator", name: "Tiana Turner", url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_parish-united/artifacts/zar2hac9_tiana%20turner.png" },
            { role: "Assistant Coach" },
            { role: "Choreographer" },
            { role: "Tumbling Instructor" },
          ].map((c) => (
            <div key={c.role} className="border border-scu-smoke/20 bg-white rounded-md p-5 hover:border-scu-purple transition-colors">
              <div className="aspect-[3/4] w-full rounded-md bg-gradient-to-b from-scu-obsidian via-scu-charcoal to-scu-smoke/60 flex items-center justify-center overflow-hidden">
                {c.url ? (
                  <img src={c.url} alt={c.name} className="h-full w-full object-cover" style={{ objectPosition: "center 20%" }} />
                ) : (
                  <span className="font-bebas text-4xl text-white/40 tracking-wide">SCU</span>
                )}
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-scu-purple font-bold">{c.role}</div>
              <div className="mt-1 font-bebas text-xl tracking-wide text-scu-obsidian">{c.name || "To Be Announced"}</div>
              <div className="text-xs text-scu-smoke mt-1">{c.name ? "" : "Bio and photo coming soon."}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-scu-off border-y border-scu-smoke/15 py-20">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            { title: "Coaching Staff", body: "Head coaches and instructors coming soon (placeholder)." },
            { title: "Practice Info", body: "Practice times, locations and uniforms announced pre-season (placeholder)." },
            { title: "Performance Events", body: "Home game performances, competitions and community events (placeholder)." },
          ].map((c) => (
            <Card key={c.title} className="border-scu-smoke/20 rounded-md p-6">
              <div className="font-bebas text-3xl tracking-wide text-scu-obsidian">{c.title}</div>
              <p className="mt-3 text-scu-smoke">{c.body}</p>
            </Card>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20" data-testid="cheer-development">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Cheerleader Development</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Building The Complete Athlete</h2>
        <p className="mt-4 max-w-4xl text-scu-smoke text-lg leading-relaxed">
          Our SCU Cheer Program is designed to develop our young ladies in every aspect — athletically, mentally, socially and personally. Through cheer, our athletes will learn the importance of <b className="text-scu-purple">teamwork, accountability, discipline, leadership, confidence and commitment</b> while understanding what it means to be part of something bigger than themselves. We want our cheerleaders to support and encourage one another, take pride in representing SCU and their community, and learn how to handle both success and adversity with character. Our goal goes beyond developing talented cheerleaders; we strive to help develop <b className="text-scu-obsidian">confident, responsible, respectful and empowered young women</b> who carry the lessons learned through SCU into the classroom, their community, and their future.
        </p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {["Teamwork","Accountability","Discipline","Leadership","Confidence","Commitment"].map((v) => (
            <div key={v} className="border border-scu-smoke/20 bg-white rounded-md p-4 text-center hover:border-scu-purple transition-colors">
              <div className="font-bebas text-xl tracking-wide text-scu-purple">{v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
